import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Earthy Overlay - Ken Burns Effect */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-gradient-to-r from-[#2D3436]/90 via-[#3D352D]/70 to-[#2C6E63]/50 z-10"
        />
        <motion.img
          initial={{ scale: 1.1 }}
          animate={{ scale: 1.0 }}
          transition={{ duration: 8, ease: "easeOut" }}
          src="/projects/2024 FOH 10.JPG"
          alt="High-end commercial kitchen stainless steel setup"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="relative z-20 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="max-w-4xl"
        >
          {/* Overline with reveal animation */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "auto" }}
            transition={{ duration: 1, delay: 0.4 }}
            className="overflow-hidden"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-[#D4C4A8] text-sm uppercase tracking-[0.3em] mb-6 whitespace-nowrap"
            >
              Since 1997
            </motion.p>
          </motion.div>
          
          {/* Staggered word reveal animation */}
          <h1
            className="font-heading text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[1.1] text-white mb-8"
            data-testid="hero-headline"
          >
            {["Delivering", "World-Class", "F&B", "Kitchen", "Solutions"].map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 + i * 0.15, ease: [0.215, 0.61, 0.355, 1] }}
                className="inline-block mr-4"
              >
                {word}
              </motion.span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="text-lg sm:text-xl font-light leading-relaxed text-[#E8E2D9] mb-12 max-w-2xl"
            data-testid="hero-subtext"
          >
            Royal Middle East International LLC combines craftsmanship, innovation and reliability to empower premium food service operations across the UAE.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollToSection('portfolio')}
              className="button-primary inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#D4C4A8] text-[#2D3436] font-medium tracking-wide hover:bg-[#C9B896] transition-all duration-300 group"
              data-testid="hero-cta-explore"
            >
              Explore Our Work
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="button-secondary inline-flex items-center justify-center gap-3 px-10 py-4 border-2 border-white/30 text-white font-medium tracking-wide hover:bg-white/10 hover:border-white/50 transition-all duration-300 group"
              data-testid="hero-cta-contact"
            >
              Contact Us
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 h-2 bg-[#D4C4A8] rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
